import React, { useState, useEffect } from 'react';
import '../../responsive.css';
import { useNavigate } from 'react-router-dom';
import { useAdmin } from '../../context/AdminContext';
import {
    AlertTriangle, BadgeCheck, CalendarDays, ChevronDown, ChevronUp, Eye,
    EyeOff, Lock, Mail, Phone, Plus, Save, ShieldCheck, Store, Trash2,
    UserPlus, UserRound, Users, X,
} from 'lucide-react';
import Sidebar from '../../components/Sidebar';
import Navbar from '../../components/Navbar';
import ConfirmDialog from '../../components/ConfirmDialog';
import MultiSelect from '../../components/MultiSelect';
import API from '../../utils/api';
import toast from 'react-hot-toast';
import '../Profiles/Profiles.css';

const initialFor = name => (name || '?').trim().charAt(0).toUpperCase() || '?';
const EMAIL_PATTERN = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

const AdminAccess = () => {
    const navigate = useNavigate();
    const { admin: signedInAdmin } = useAdmin();
    const [admins, setAdmins] = useState([]);
    const [showForm, setShowForm] = useState(false);
    const [form, setForm] = useState({
        name: '', email: '', mobile: '', password: '', canVerifyProfiles: false,
    });
    const [pendingDelete, setPendingDelete] = useState(null);
    const [showPassword, setShowPassword] = useState(false);
    // Unverified members + unverified service providers combined — the pool
    // an assign panel picks from, and how "assigned to me" counts/badges get
    // computed per verifier admin. Both are User documents (role: 'member'
    // or 'service'), so one list/field covers both — see User.js.
    const [unverifiedAccounts, setUnverifiedAccounts] = useState([]);
    const [expandedAssignId, setExpandedAssignId] = useState(null);
    const [assignSelection, setAssignSelection] = useState([]);
    const [savingAssignment, setSavingAssignment] = useState(false);

    // No local loading flag needed — every call through utils/api.js already
    // reports to the global loader, which shows the theme-matched honeycomb
    // overlay while this is in flight.
    useEffect(() => { fetchAdmins(); fetchUnverifiedAccounts(); }, []);

    const fetchAdmins = async () => {
        try {
            const res = await API.get('/admin/admins');
            setAdmins(res.data.admins || []);
        } catch (err) {
            toast.error('Failed to load admins');
        }
    };

    const fetchUnverifiedAccounts = async () => {
        try {
            const [usersRes, vendorsRes, profilesRes] = await Promise.all([
                API.get('/admin/users'),
                API.get('/admin/vendors'),
                API.get('/admin/profiles'),
            ]);
            // ProfileDetail.jsx is keyed by Profile._id, not the linked
            // User._id — this map lets a member's "View" button land on the
            // right profile instead of just knowing their account exists.
            const profileIdByUserId = {};
            (profilesRes.data.profiles || []).forEach(p => {
                if (p.user?._id) profileIdByUserId[p.user._id] = p._id;
            });
            const members = (usersRes.data.users || [])
                .filter(u => !u.isVerified)
                .map(u => ({
                    ...u, roleLabel: 'Member',
                    detailPath: profileIdByUserId[u._id] ? `/profiles/${profileIdByUserId[u._id]}` : null,
                }));
            const providers = (vendorsRes.data.vendors || [])
                .filter(v => !v.isVerified)
                .map(v => ({ ...v, roleLabel: 'Provider', name: v.businessName, detailPath: `/service-providers/${v._id}` }));
            const combined = [...members, ...providers]
                .sort((a, b) => new Date(b.createdAt || 0) - new Date(a.createdAt || 0));
            setUnverifiedAccounts(combined);
        } catch (err) {
            toast.error('Failed to load unverified accounts');
        }
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        if (form.mobile.length !== 10) {
            toast.error('Enter a valid 10-digit mobile number');
            return;
        }
        if (!EMAIL_PATTERN.test(form.email)) {
            toast.error('Enter a valid email address');
            return;
        }
        if (form.password.length < 6) {
            toast.error('Password must be at least 6 characters!');
            return;
        }
        try {
            await API.post('/admin/admins', form);
            toast.success('Admin account created!');
            setShowForm(false);
            setForm({ name: '', email: '', mobile: '', password: '', canVerifyProfiles: false });
            setShowPassword(false);
            fetchAdmins();
        } catch (err) {
            toast.error(err.response?.data?.message || 'Failed!');
        }
    };

    const confirmDeleteAdmin = async () => {
        const admin = pendingDelete;
        if (!admin) return;
        setPendingDelete(null);
        try {
            await API.delete(`/admin/admins/${admin._id}`);
            toast.success('Admin removed!');
            fetchAdmins();
        } catch (err) {
            toast.error('Failed!');
        }
    };

    const handleToggleVerifier = async (id) => {
        try {
            const res = await API.put(`/admin/admins/${id}/toggle-verifier`);
            toast.success(res.data.message);
            fetchAdmins();
        } catch (err) {
            toast.error('Failed!');
        }
    };

    const assignedAccountIdsFor = adminId => unverifiedAccounts
        .filter(a => (typeof a.assignedVerifier === 'string' ? a.assignedVerifier : a.assignedVerifier?._id) === adminId)
        .map(a => a._id);

    const currentAdminId = String(signedInAdmin?.id || signedInAdmin?._id || '');
    const currentAdmin = admins.find(admin => String(admin._id) === currentAdminId);
    const currentAssignments = currentAdmin ? unverifiedAccounts.filter(account => assignedAccountIdsFor(currentAdmin._id).includes(account._id)) : [];
    const adminsForList = currentAdmin ? admins.filter(admin => String(admin._id) !== currentAdminId) : admins;

    const toggleAssignPanel = admin => {
        if (expandedAssignId === admin._id) {
            setExpandedAssignId(null);
            return;
        }
        setExpandedAssignId(admin._id);
        setAssignSelection(assignedAccountIdsFor(admin._id));
    };

    const handleSaveAssignment = async adminId => {
        setSavingAssignment(true);
        try {
            const res = await API.put(`/admin/admins/${adminId}/assign-members`, { userIds: assignSelection });
            toast.success(res.data.message);
            setExpandedAssignId(null);
            fetchUnverifiedAccounts();
        } catch (err) {
            toast.error(err.response?.data?.message || 'Failed to save assignment');
        } finally {
            setSavingAssignment(false);
        }
    };

    return (
        <div style={styles.layout}>
            <Sidebar />
            <div className="admin-content" style={styles.content}>
                <Navbar title={<span style={styles.navTitle}><ShieldCheck size={18} /> Admin Access Management</span>} />
                <div className="resp-inner" style={styles.inner}>

                    {/* Info Banner */}
                    <div className="resp-banner" style={styles.infoBanner}>
                        <div style={{ display: 'flex', alignItems: 'center', gap: '16px' }}>
                            <div style={styles.bannerIcon}><ShieldCheck size={26} /></div>
                            <div>
                                <h2 style={styles.bannerTitle}>Admin Access Control</h2>
                                <p style={styles.bannerSubtitle}>
                                    Create and manage admin accounts. All admins have full access to this panel.
                                </p>
                            </div>
                        </div>
                        <div className="resp-banner-stats" style={styles.bannerStats}>
                            <div style={styles.bannerStat}>
                                <div style={styles.bannerStatValue}>{admins.length}</div>
                                <div style={styles.bannerStatLabel}>Total Admins</div>
                            </div>
                        </div>
                    </div>

                    {/* Top Row */}
                    <div className="resp-top-row" style={styles.topRow}>
                        <div>
                            <h3 style={styles.topRowTitle}>Admin Accounts ({admins.length})</h3>
                            <p style={styles.topRowSubtitle}>These accounts have full access to the admin panel</p>
                        </div>
                        <button className="profiles-btn" style={styles.addBtn} onClick={() => setShowForm(!showForm)}>
                            {showForm ? <><X size={15} /> Cancel</> : <><Plus size={15} /> Create Admin Account</>}
                        </button>
                    </div>

                    {/* Create Form */}
                    {showForm && (
                        <div className="profiles-card" style={styles.formCard}>
                            <h3 style={styles.formTitle}><UserPlus size={16} /> Create New Admin Account</h3>
                            <form onSubmit={handleSubmit}>
                                <div className="resp-grid-2" style={styles.formGrid}>
                                    <div>
                                        <label style={styles.label}>Full Name *</label>
                                        <input className="profiles-input" style={styles.input} placeholder="Admin full name"
                                            value={form.name}
                                            onChange={e => setForm({ ...form, name: e.target.value })}
                                            required />
                                    </div>
                                    <div>
                                        <label style={styles.label}>Mobile Number *</label>
                                        <input className="profiles-input" style={styles.input} placeholder="10 digit mobile number"
                                            type="tel" inputMode="numeric" pattern="[0-9]*" maxLength={10}
                                            value={form.mobile}
                                            onChange={e => setForm({ ...form, mobile: e.target.value.replace(/\D/g, '').slice(0, 10) })}
                                            required />
                                    </div>
                                    <div>
                                        <label style={styles.label}>Email Address *</label>
                                        <input type="email" className="profiles-input" style={styles.input} placeholder="admin@gettimelam.com"
                                            value={form.email}
                                            onChange={e => setForm({ ...form, email: e.target.value })}
                                            required />
                                    </div>
                                    <div>
                                        <label style={styles.label}>Password *</label>
                                        <div style={styles.passwordWrap}>
                                            <input type={showPassword ? 'text' : 'password'} className="profiles-input" style={{ ...styles.input, paddingRight: '38px' }} placeholder="Min 6 characters"
                                                value={form.password}
                                                onChange={e => setForm({ ...form, password: e.target.value })}
                                                required />
                                            <button type="button" style={styles.passwordToggle}
                                                onClick={() => setShowPassword(v => !v)}
                                                aria-label={showPassword ? 'Hide password' : 'Show password'}>
                                                {showPassword ? <EyeOff size={16} /> : <Eye size={16} />}
                                            </button>
                                        </div>
                                    </div>
                                </div>

                                <label style={{ ...styles.checkboxRow, background: 'var(--admin-primary-soft)' }}>
                                    <input type="checkbox"
                                        checked={form.canVerifyProfiles}
                                        onChange={e => setForm({ ...form, canVerifyProfiles: e.target.checked })} />
                                    <BadgeCheck size={15} style={{ flexShrink: 0, marginTop: '1px' }} />
                                    <span>Can verify profiles — this admin can be assigned to review and verify member profiles (Aadhar) and approve service providers</span>
                                </label>

                                <div style={styles.formNote}>
                                    <AlertTriangle size={15} style={{ flexShrink: 0, marginTop: '1px' }} />
                                    <span><strong>Important:</strong> This account will have <strong>full admin access</strong> to the panel including user management, bookings, and financial data. Share credentials securely.</span>
                                </div>

                                <div className="resp-form-actions" style={{ display: 'flex', gap: '12px' }}>
                                    <button type="submit" className="profiles-btn" style={styles.submitBtn}>
                                        <ShieldCheck size={15} /> Create Admin Account
                                    </button>
                                    <button type="button" className="profiles-btn" style={styles.cancelBtn}
                                        onClick={() => setShowForm(false)}>
                                        Cancel
                                    </button>
                                </div>
                            </form>
                        </div>
                    )}

                    {currentAdmin && (
                        <section className="profiles-card" style={styles.myQueueCard}>
                            <div className="resp-card-top" style={styles.myQueueHeader}>
                                <div style={styles.adminLeft}>
                                    <div style={styles.myQueueAvatar}>{initialFor(currentAdmin.name)}</div>
                                    <div>
                                        <span style={styles.myQueueEyebrow}><BadgeCheck size={13} /> Signed-in admin</span>
                                        <div style={styles.adminName}>{currentAdmin.name}</div>
                                        <div style={styles.adminMeta}><Mail size={12} /> {currentAdmin.email}</div>
                                    </div>
                                </div>
                                {currentAdmin.canVerifyProfiles ? (
                                    <div style={styles.myQueueCount}><Users size={16} /><strong>{currentAssignments.length}</strong> assigned to me</div>
                                ) : (
                                    <button type="button" className="profiles-btn" style={styles.toggleVerifierBtn} onClick={() => handleToggleVerifier(currentAdmin._id)}>Make me verifier</button>
                                )}
                            </div>
                            {currentAdmin.canVerifyProfiles ? (
                                currentAssignments.length > 0 ? (
                                    <div style={styles.assignedList}>
                                        {currentAssignments.map(account => {
                                            const RoleIcon = account.roleLabel === 'Provider' ? Store : UserRound;
                                            return (
                                                <div key={account._id} className="resp-row-wrap" style={styles.assignedRow}>
                                                    <div style={styles.assignedRowAvatar}>{initialFor(account.name)}</div>
                                                    <div style={styles.assignedRowInfo}>
                                                        <div style={styles.assignedRowName}><RoleIcon size={12} /> {account.name || 'Unnamed'}<span style={styles.assignedRowRole}>{account.roleLabel}</span></div>
                                                        <div style={styles.assignedRowMeta}>{account.mobile && <span style={styles.assignedRowMetaItem}><Phone size={11} /> {account.mobile}</span>}{account.email && <span style={styles.assignedRowMetaItem}><Mail size={11} /> {account.email}</span>}</div>
                                                    </div>
                                                    <button type="button" className="profiles-btn" style={{ ...styles.assignedRowViewBtn, opacity: account.detailPath ? 1 : 0.5, cursor: account.detailPath ? 'pointer' : 'not-allowed' }} disabled={!account.detailPath} onClick={() => account.detailPath && navigate(account.detailPath)}><Eye size={13} /> View & Verify</button>
                                                </div>
                                            );
                                        })}
                                    </div>
                                ) : <p style={styles.hint}>No unverified members or providers are assigned to you right now.</p>
                            ) : <p style={styles.hint}>You are not marked as a Profile Verifier yet. Select “Make me verifier” above before assignments can be given to you.</p>}
                        </section>
                    )}

                    {/* Admins List */}
                    {admins.length === 0 ? (
                        <div className="profiles-card" style={styles.empty}>
                            <ShieldCheck size={44} color="var(--admin-text-muted)" />
                            <h3 style={styles.emptyTitle}>No Other Admin Accounts</h3>
                            <p style={styles.emptyText}>Create admin accounts to give others access to this panel</p>
                        </div>
                    ) : (
                        <div>
                            {adminsForList.map((admin) => {
                                const assignedCount = assignedAccountIdsFor(admin._id).length;
                                const isExpanded = expandedAssignId === admin._id;
                                return (
                                    <div key={admin._id} className="profiles-card" style={styles.adminCard}>
                                        <div className="resp-card-top" style={styles.adminCardTop}>
                                            <div style={styles.adminLeft}>
                                                <div style={styles.adminAvatar}>{initialFor(admin.name)}</div>
                                                <div>
                                                    <div style={styles.adminName}>{admin.name}</div>
                                                    <div style={styles.adminMeta}><Phone size={12} /> {admin.mobile}</div>
                                                    <div style={styles.adminMeta}><Mail size={12} /> {admin.email}</div>
                                                    <div style={styles.adminMeta}>
                                                        <CalendarDays size={12} /> Created: {new Date(admin.createdAt).toLocaleDateString('en-IN', {
                                                            day: 'numeric', month: 'long', year: 'numeric'
                                                        })}
                                                    </div>
                                                </div>
                                            </div>
                                            <div className="resp-card-actions" style={styles.adminRight}>
                                                <span style={styles.adminBadge}><ShieldCheck size={12} /> Full Admin</span>
                                                {admin.canVerifyProfiles && (
                                                    <span style={styles.verifierBadge}><BadgeCheck size={12} /> Profile Verifier</span>
                                                )}
                                                {admin.canVerifyProfiles && (
                                                    <button className="profiles-btn" style={styles.assignBtn}
                                                        onClick={() => toggleAssignPanel(admin)}>
                                                        <Users size={13} /> Assign Members ({assignedCount})
                                                        {isExpanded ? <ChevronUp size={13} /> : <ChevronDown size={13} />}
                                                    </button>
                                                )}
                                                <button className="profiles-btn" style={styles.toggleVerifierBtn}
                                                    onClick={() => handleToggleVerifier(admin._id)}>
                                                    {admin.canVerifyProfiles ? 'Remove as Verifier' : 'Make Verifier'}
                                                </button>
                                                <button className="profiles-btn" style={styles.deleteBtn}
                                                    onClick={() => setPendingDelete(admin)}>
                                                    <Trash2 size={13} /> Remove Access
                                                </button>
                                            </div>
                                        </div>

                                        {isExpanded && (
                                            <div style={styles.assignPanel}>
                                                <label style={styles.label}>
                                                    Unverified accounts assigned to {admin.name} ({unverifiedAccounts.length} unverified total)
                                                </label>
                                                <MultiSelect
                                                    value={assignSelection}
                                                    onChange={setAssignSelection}
                                                    options={unverifiedAccounts.map(a => ({
                                                        value: a._id,
                                                        label: a.name || 'Unnamed',
                                                        meta: `${a.roleLabel} · ${a.createdAt ? new Date(a.createdAt).toLocaleDateString('en-IN') : ''}`,
                                                    }))}
                                                    placeholder="Select members or providers"
                                                    searchPlaceholder="Search by name..."
                                                />
                                                {unverifiedAccounts.length === 0 && (
                                                    <p style={styles.hint}>Nothing unverified right now — everyone's caught up.</p>
                                                )}

                                                {assignSelection.length > 0 && (
                                                    <div style={styles.assignedList}>
                                                        {assignSelection.map(id => {
                                                            const acc = unverifiedAccounts.find(a => a._id === id);
                                                            if (!acc) return null;
                                                            const RoleIcon = acc.roleLabel === 'Provider' ? Store : UserRound;
                                                            return (
                                                                <div key={id} className="resp-row-wrap" style={styles.assignedRow}>
                                                                    <div style={styles.assignedRowAvatar}>{initialFor(acc.name)}</div>
                                                                    <div style={styles.assignedRowInfo}>
                                                                        <div style={styles.assignedRowName}>
                                                                            <RoleIcon size={12} /> {acc.name || 'Unnamed'}
                                                                            <span style={styles.assignedRowRole}>{acc.roleLabel}</span>
                                                                        </div>
                                                                        <div style={styles.assignedRowMeta}>
                                                                            {acc.mobile && <span style={styles.assignedRowMetaItem}><Phone size={11} /> {acc.mobile}</span>}
                                                                            {acc.email && <span style={styles.assignedRowMetaItem}><Mail size={11} /> {acc.email}</span>}
                                                                        </div>
                                                                    </div>
                                                                    <button type="button" className="profiles-btn"
                                                                        style={{ ...styles.assignedRowViewBtn, opacity: acc.detailPath ? 1 : 0.5, cursor: acc.detailPath ? 'pointer' : 'not-allowed' }}
                                                                        disabled={!acc.detailPath}
                                                                        title={acc.detailPath ? 'View full profile' : 'No profile on file yet'}
                                                                        onClick={() => acc.detailPath && navigate(acc.detailPath)}>
                                                                        <Eye size={13} /> View & Verify
                                                                    </button>
                                                                </div>
                                                            );
                                                        })}
                                                    </div>
                                                )}

                                                <div className="resp-form-actions" style={styles.assignActions}>
                                                    <button type="button" className="profiles-btn" style={styles.cancelBtn}
                                                        onClick={() => setExpandedAssignId(null)}>
                                                        Cancel
                                                    </button>
                                                    <button type="button" className="profiles-btn"
                                                        style={{ ...styles.submitBtn, opacity: savingAssignment ? 0.7 : 1 }}
                                                        disabled={savingAssignment}
                                                        onClick={() => handleSaveAssignment(admin._id)}>
                                                        <Save size={14} /> {savingAssignment ? 'Saving...' : 'Save Assignment'}
                                                    </button>
                                                </div>
                                            </div>
                                        )}
                                    </div>
                                );
                            })}
                        </div>
                    )}

                    {/* Security Note */}
                    <div className="profiles-card" style={styles.securityNote}>
                        <h4 style={styles.securityTitle}><Lock size={15} /> Security Guidelines</h4>
                        <ul style={styles.securityList}>
                            <li>Only create admin accounts for trusted team members</li>
                            <li>Use strong passwords with letters, numbers and symbols</li>
                            <li>Remove admin access immediately when no longer needed</li>
                            <li>Never share admin credentials via email or chat</li>
                            <li>Regularly review and audit admin accounts</li>
                        </ul>
                    </div>

                </div>
            </div>

            <ConfirmDialog
                open={Boolean(pendingDelete)}
                title="Remove admin access?"
                message={pendingDelete ? `Remove admin access for ${pendingDelete.name}? They will immediately lose access to this panel.` : ''}
                confirmLabel="Remove Access"
                danger
                onConfirm={confirmDeleteAdmin}
                onCancel={() => setPendingDelete(null)}
            />
        </div>
    );
};

const styles = {
    layout: { display: 'flex', minHeight: '100vh', background: 'var(--admin-bg)' },
    content: { flex: 1, minWidth: 0 },
    inner: { padding: '28px' },
    navTitle: { display: 'inline-flex', alignItems: 'center', gap: '8px' },
    infoBanner: {
        background: 'linear-gradient(135deg, var(--admin-primary), var(--admin-primary-strong))',
        borderRadius: '16px', padding: '28px', marginBottom: '24px',
        display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: '16px',
    },
    bannerIcon: {
        width: '54px', height: '54px', borderRadius: '14px', background: 'rgba(255,255,255,0.15)',
        display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#fff', flexShrink: 0,
    },
    bannerTitle: { color: '#fff', marginBottom: '4px', fontSize: '20px' },
    bannerSubtitle: { color: 'rgba(255,255,255,0.75)', fontSize: '14px' },
    bannerStats: { display: 'flex', gap: '24px' },
    bannerStat: { textAlign: 'center', background: 'rgba(255,255,255,0.12)', padding: '16px 24px', borderRadius: '12px' },
    bannerStatValue: { fontSize: '32px', fontWeight: '700', color: '#fff' },
    bannerStatLabel: { fontSize: '12px', color: 'rgba(255,255,255,0.7)', marginTop: '4px' },
    topRow: { display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '20px', flexWrap: 'wrap', gap: '12px' },
    topRowTitle: { fontSize: '16px', fontWeight: '700', color: 'var(--admin-text)' },
    topRowSubtitle: { fontSize: '13px', color: 'var(--admin-text-secondary)', marginTop: '4px' },
    addBtn: {
        display: 'inline-flex', alignItems: 'center', gap: '6px', padding: '10px 20px',
        background: 'var(--admin-primary)', color: '#fff', border: 'none', borderRadius: '8px',
        fontSize: '14px', fontWeight: '600', cursor: 'pointer',
    },
    formCard: { padding: '24px', marginBottom: '24px' },
    formTitle: { display: 'flex', alignItems: 'center', gap: '7px', fontSize: '16px', fontWeight: '700', color: 'var(--admin-text)', marginBottom: '20px' },
    formGrid: { display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '16px', marginBottom: '16px' },
    label: { display: 'block', fontSize: '11px', fontWeight: '700', color: 'var(--admin-text-secondary)', marginBottom: '5px', textTransform: 'uppercase', letterSpacing: '0.5px' },
    input: { width: '100%', padding: '10px 14px', border: '1.5px solid var(--admin-border-strong)', borderRadius: '8px', fontSize: '14px', outline: 'none', boxSizing: 'border-box', background: 'var(--admin-surface)', color: 'var(--admin-text)' },
    passwordWrap: { position: 'relative' },
    passwordToggle: {
        position: 'absolute', right: '4px', top: '50%', transform: 'translateY(-50%)',
        display: 'flex', alignItems: 'center', justifyContent: 'center', width: '30px', height: '30px',
        background: 'none', border: 'none', color: 'var(--admin-text-secondary)', cursor: 'pointer', borderRadius: '6px',
    },
    checkboxRow: {
        display: 'flex', alignItems: 'flex-start', gap: '10px', fontSize: '13px', color: 'var(--admin-text)',
        background: 'var(--admin-surface-muted)', padding: '12px 14px', borderRadius: '8px', marginBottom: '12px',
        cursor: 'pointer', lineHeight: 1.5,
    },
    formNote: {
        display: 'flex', alignItems: 'flex-start', gap: '8px',
        background: '#FFF8E1', border: '1px solid #FFE082', borderRadius: '8px', padding: '12px 16px',
        fontSize: '13px', color: '#F57F17', marginBottom: '16px', lineHeight: 1.6,
    },
    submitBtn: { display: 'inline-flex', alignItems: 'center', gap: '6px', padding: '12px 28px', background: 'var(--admin-primary)', color: '#fff', border: 'none', borderRadius: '8px', fontSize: '14px', fontWeight: '600', cursor: 'pointer' },
    cancelBtn: { padding: '12px 20px', background: 'var(--admin-surface)', color: 'var(--admin-text-secondary)', border: '1px solid var(--admin-border-strong)', borderRadius: '8px', fontSize: '14px', fontWeight: '600', cursor: 'pointer' },
    adminCard: { padding: '20px', marginBottom: '12px' },
    myQueueCard: { padding: '20px', marginBottom: '18px', border: '1px solid color-mix(in srgb, var(--admin-primary) 38%, var(--admin-border))', background: 'linear-gradient(135deg, var(--admin-primary-soft), var(--admin-surface) 58%)' },
    myQueueHeader: { display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: '14px' },
    myQueueAvatar: { width: '52px', height: '52px', borderRadius: '50%', flexShrink: 0, background: 'var(--admin-primary)', color: '#fff', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '22px', fontWeight: '700', boxShadow: '0 8px 18px color-mix(in srgb, var(--admin-primary) 24%, transparent)' },
    myQueueEyebrow: { display: 'inline-flex', alignItems: 'center', gap: '5px', marginBottom: '4px', color: 'var(--admin-primary)', fontSize: '10px', fontWeight: '800', letterSpacing: '.06em', textTransform: 'uppercase' },
    myQueueCount: { display: 'inline-flex', alignItems: 'center', gap: '7px', padding: '8px 12px', borderRadius: '999px', background: 'var(--admin-surface)', color: 'var(--admin-primary)', fontSize: '12px', fontWeight: '700', border: '1px solid var(--admin-border)' },
    adminCardTop: { display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: '14px' },
    adminLeft: { display: 'flex', gap: '16px', alignItems: 'flex-start' },
    adminAvatar: {
        width: '52px', height: '52px', borderRadius: '50%', flexShrink: 0,
        background: 'var(--admin-primary)', color: '#fff',
        display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '22px', fontWeight: '700',
    },
    adminName: { fontWeight: '700', color: 'var(--admin-text)', fontSize: '16px', marginBottom: '4px' },
    adminMeta: { display: 'flex', alignItems: 'center', gap: '5px', fontSize: '12px', color: 'var(--admin-text-secondary)', marginBottom: '2px' },
    adminRight: { display: 'flex', gap: '10px', alignItems: 'center', flexShrink: 0, flexWrap: 'wrap' },
    adminBadge: { display: 'inline-flex', alignItems: 'center', gap: '4px', background: 'var(--admin-primary-soft)', color: 'var(--admin-primary)', padding: '6px 14px', borderRadius: '20px', fontSize: '13px', fontWeight: '700' },
    verifierBadge: { display: 'inline-flex', alignItems: 'center', gap: '4px', background: '#E8F5E9', color: '#2E7D32', padding: '6px 14px', borderRadius: '20px', fontSize: '13px', fontWeight: '700' },
    toggleVerifierBtn: { background: 'var(--admin-surface)', border: '1.5px solid #2E7D32', color: '#2E7D32', padding: '6px 12px', borderRadius: '8px', fontSize: '12px', fontWeight: '600', cursor: 'pointer' },
    assignBtn: {
        display: 'inline-flex', alignItems: 'center', gap: '5px',
        background: 'var(--admin-primary-soft)', border: 'none', color: 'var(--admin-primary)',
        padding: '6px 12px', borderRadius: '8px', fontSize: '12px', fontWeight: '600', cursor: 'pointer',
    },
    assignPanel: {
        marginTop: '16px', paddingTop: '16px', borderTop: '1px solid var(--admin-border)',
    },
    assignActions: { display: 'flex', gap: '10px', justifyContent: 'flex-end', marginTop: '14px' },
    hint: { fontSize: '12px', color: 'var(--admin-text-muted)', marginTop: '6px' },
    assignedList: { display: 'flex', flexDirection: 'column', gap: '8px', marginTop: '16px' },
    assignedRow: {
        display: 'flex', alignItems: 'center', gap: '12px', padding: '10px 12px',
        background: 'var(--admin-surface-muted)', borderRadius: '10px',
    },
    assignedRowAvatar: {
        width: '34px', height: '34px', borderRadius: '50%', flexShrink: 0,
        background: 'var(--admin-primary)', color: '#fff',
        display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '14px', fontWeight: '700',
    },
    assignedRowInfo: { flex: 1, minWidth: 0 },
    assignedRowName: { display: 'flex', alignItems: 'center', gap: '5px', fontSize: '13px', fontWeight: '700', color: 'var(--admin-text)' },
    assignedRowRole: {
        fontSize: '10px', fontWeight: '700', color: 'var(--admin-text-secondary)', textTransform: 'uppercase',
        background: 'var(--admin-surface)', border: '1px solid var(--admin-border)', borderRadius: '20px', padding: '1px 7px', marginLeft: '4px',
    },
    assignedRowMeta: { display: 'flex', gap: '12px', fontSize: '11.5px', color: 'var(--admin-text-secondary)', marginTop: '2px' },
    assignedRowMetaItem: { display: 'inline-flex', alignItems: 'center', gap: '4px' },
    assignedRowViewBtn: {
        display: 'inline-flex', alignItems: 'center', gap: '5px', flexShrink: 0,
        background: 'var(--admin-primary-soft)', color: 'var(--admin-primary)', border: 'none',
        padding: '7px 12px', borderRadius: '7px', fontSize: '12px', fontWeight: '600', cursor: 'pointer',
    },
    deleteBtn: { display: 'inline-flex', alignItems: 'center', gap: '5px', padding: '8px 16px', background: 'none', color: '#B71C1C', border: '1.5px solid #B71C1C', borderRadius: '8px', fontSize: '13px', fontWeight: '600', cursor: 'pointer' },
    empty: { textAlign: 'center', padding: '60px', display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '10px' },
    emptyTitle: { color: 'var(--admin-text)', margin: 0 },
    emptyText: { color: 'var(--admin-text-secondary)', fontSize: '13px', margin: 0 },
    securityNote: { padding: '20px', marginTop: '20px' },
    securityTitle: { display: 'flex', alignItems: 'center', gap: '7px', color: 'var(--admin-text)', marginBottom: '8px' },
    securityList: { color: 'var(--admin-text-secondary)', fontSize: '13px', paddingLeft: '16px', lineHeight: 1.8, margin: 0 },
};

export default AdminAccess;
